import type { Translations } from './en';

/**
 * Hungarian strings. Primary language of the site — this is what `/` serves.
 *
 * Typed as `Translations`, so if a key is added to `en.ts` and forgotten here,
 * `npm run typecheck` fails. That is deliberate: a half-translated page is worse than an
 * obviously missing one.
 */
export const hu: Translations = {
  'site.title': 'Pálinka Kalkulátor',
  'site.tagline': 'Kalkulátorok és útmutatók pálinkafőzéshez',
  'site.description':
    'Ingyenes, böngészőben futó kalkulátorok hígításhoz, házasításhoz, alkoholmérő hőmérséklet-korrekciójához és cefre kihozatalához, útmutatókkal pálinkafőzőknek.',

  'nav.home': 'Kezdőlap',
  'nav.calculators': 'Kalkulátorok',
  'nav.guides': 'Útmutatók',
  'nav.language': 'Nyelv',
  'nav.skipToContent': 'Ugrás a tartalomra',
  'nav.toggleTheme': 'Váltás világos és sötét téma között',

  'common.results': 'Eredmények',
  'common.method': 'Módszer és pontosság',
  'common.reset': 'Alaphelyzet',
  'common.readMore': 'Tovább',
  'common.backToGuides': 'Vissza az útmutatókhoz',
  'common.backToCalculators': 'Vissza a kalkulátorokhoz',
  'common.sourceOnGitHub': 'Forráskód a GitHubon',
  'common.copyright': '© 2024–2026 Fuksz Domonkos',
  'common.calculatorWord': 'kalkulátor',
  'common.breadcrumb': 'Morzsamenü',
  'common.relatedGuide': 'Kapcsolódó útmutató',
  'common.relatedCalculator': 'Kapcsolódó kalkulátor',
  'common.otherCalculators': 'További kalkulátorok',
  'common.updated': 'Frissítve',
  'common.disclaimer':
    'A kalkulátorok tervezéshez és tájékozódáshoz készültek. Jövedéki, vámügyi vagy bármilyen hatósági bevalláshoz nem használhatók.',
  'common.liveRegionLabel': 'Számítás eredménye',

  'unit.percent': 'V/V%',
  'unit.litre': 'l',
  'unit.celsius': '°C',
  'unit.kilogram': 'kg',

  'error.notANumber': 'Adjon meg egy számot.',
  'error.abvRange': 'Az alkoholtartalomnak 0 és 100 V/V% között kell lennie.',
  'error.targetAboveSource':
    'A cél alkoholtartalom nem lehet nagyobb a jelenlegi {abv} V/V%-nál — a víz csak gyengíti a párlatot.',
  'error.volumePositive': 'A mennyiségnek nullánál nagyobbnak kell lennie.',
  'error.volumeNonNegative': 'A mennyiség nem lehet negatív.',
  'error.massPositive': 'A tömegnek nullánál nagyobbnak kell lennie.',
  'error.percentRange': 'Adjon meg egy 0 és 100 közötti százalékot.',
  'error.readingOutOfRange':
    'A korrekció csak {min} és {max} V/V% között megbízható. Ezen kívül használja a hivatalos táblázatokat.',
  'error.tempOutOfRange':
    'A korrekció csak {min} és {max} °C között megbízható. Ezen kívül használja a hivatalos táblázatokat.',

  'notes.idealMixing':
    'Ideális elegyedést feltételez. Az etanol és a víz összekeverve kis mértékben összehúzódik — leginkább 50 V/V% környékén —, így a valós térfogat kissé kisebb a számítottnál, legfeljebb mintegy 2–3%-kal. Adjon hozzá valamivel kevesebb vizet a megadottnál, mérje meg, majd pótolja.',
  'notes.tempApproximation':
    'Lineáris közelítést használ: 0,33 V/V% fokonként a 20 °C-tól való eltérésre. A valódi korrekció az alkoholtartalomtól is függ, ezért néhány tized százalék eltérés várható. Hivatalos célra használja az OIML alkoholometriás táblázatokat.',
  'notes.absoluteAlcohol':
    'Ez a számítás pontos — nincs benne elegyedés, így a bizonytalanság csak abból ered, milyen pontosan mérte meg az alkoholtartalmat. A bejelentési határok és kedvezmények országonként és időben változnak; ez mennyiséget ad meg, nem kötelezettséget.',
  'notes.sugarYield':
    'Tervezési becslés. A hatásfok tartalmaz mindent, amit a valódi erjesztés és lepárlás elveszít: az élesztő szaporodását, az erjeszthetetlen maradékot, az üstben maradó alkoholt, valamint az elvetett előpárlatot és utópárlatot. Mérje meg a saját kihozatalát, és igazítsa hozzá a hatásfokot.',

  'calc.dilution.slug': 'higitas',
  'calc.dilution.name': 'Hígítás',
  'calc.dilution.description':
    'Mennyi vizet kell hozzáadni, hogy a párlat a kívánt fokra híguljon.',
  'calc.dilution.field.volume': 'Párlat mennyisége',
  'calc.dilution.field.abv': 'Jelenlegi alkoholtartalom',
  'calc.dilution.field.targetAbv': 'Cél alkoholtartalom',
  'calc.dilution.output.waterToAdd': 'Hozzáadandó víz',
  'calc.dilution.output.finalVolume': 'Végső mennyiség',
  'calc.dilution.output.absoluteAlcohol': 'Tiszta alkohol (változatlan)',

  'calc.blend.slug': 'hazasitas',
  'calc.blend.name': 'Házasítás',
  'calc.blend.description': 'Milyen fok jön ki két párlat összeöntéséből.',
  'calc.blend.field.volumeA': 'Első párlat mennyisége',
  'calc.blend.field.abvA': 'Első párlat alkoholtartalma',
  'calc.blend.field.volumeB': 'Második párlat mennyisége',
  'calc.blend.field.abvB': 'Második párlat alkoholtartalma',
  'calc.blend.output.abv': 'Eredő alkoholtartalom',
  'calc.blend.output.totalVolume': 'Összes mennyiség',
  'calc.blend.output.absoluteAlcohol': 'Tiszta alkohol',

  'calc.tempCorrection.slug': 'homerseklet-korrekcio',
  'calc.tempCorrection.name': 'Hőmérséklet-korrekció',
  'calc.tempCorrection.description':
    'Az alkoholmérők 20 °C-ra vannak hitelesítve. Számítsa át a más hőmérsékleten leolvasott értéket.',
  'calc.tempCorrection.field.reading': 'Alkoholmérő leolvasott értéke',
  'calc.tempCorrection.field.temperature': 'A párlat hőmérséklete',
  'calc.tempCorrection.output.correctedAbv': 'Valós alkoholtartalom 20 °C-on',
  'calc.tempCorrection.output.correction': 'Alkalmazott korrekció',
  'calc.tempCorrection.output.deviation': 'Eltérés 20 °C-tól',

  'calc.absoluteAlcohol.slug': 'abszolut-alkohol',
  'calc.absoluteAlcohol.name': 'Abszolút alkohol',
  'calc.absoluteAlcohol.description':
    'Hány liter tiszta etanol van a párlatban — ebben az egységben tartják nyilván a mennyiséget.',
  'calc.absoluteAlcohol.field.volume': 'Párlat mennyisége',
  'calc.absoluteAlcohol.field.abv': 'Alkoholtartalom',
  'calc.absoluteAlcohol.output.absoluteAlcohol': 'Abszolút alkohol',
  'calc.absoluteAlcohol.output.equivalentAt50': 'Ennyi 50 V/V%-os párlat',
  'calc.absoluteAlcohol.output.ethanolMass': 'Etanol tömege',

  'calc.sugarYield.slug': 'cefre-kihozatal',
  'calc.sugarYield.name': 'Cefre kihozatala',
  'calc.sugarYield.description':
    'Nagyjából mennyi pálinkát ad a cefre a cukortartalma alapján.',
  'calc.sugarYield.field.mashMass': 'Cefre tömege',
  'calc.sugarYield.field.sugarContent': 'Cukortartalom',
  'calc.sugarYield.field.efficiency': 'Teljes hatásfok',
  'calc.sugarYield.output.spiritAt50': 'Kész párlat 50 V/V%-on',
  'calc.sugarYield.output.pureEthanol': 'Tiszta alkohol',
  'calc.sugarYield.output.sugarMass': 'Erjeszthető cukor',
  'calc.sugarYield.output.theoreticalMax': 'Elméleti maximum',

  'page.home.title': 'Pálinka Kalkulátor',
  'page.home.intro':
    'Gyakorlati kalkulátorok pálinkafőzéshez — hígítás, házasítás, alkoholmérő-korrekció és cefre kihozatala —, valamint útmutatók a mögöttük álló számításokról. Minden a böngészőjében fut; amit beír, sehová nem kerül el.',
  'page.home.calculatorsHeading': 'Kalkulátorok',
  'page.home.guidesHeading': 'Útmutatók',

  'page.calculators.title': 'Kalkulátorok',
  'page.calculators.intro':
    'Minden kalkulátor megadja a módszerét és a korlátait. Ahol a képlet közelítés, ott ezt kiírja.',

  'page.guides.title': 'Útmutatók',
  'page.guides.intro': 'A kalkulátorok mögötti gondolatmenet, és hogyan érdemes használni őket.',

  'page.notFound.title': 'A keresett oldal nem található',
  'page.notFound.intro':
    'Ezen a címen nincs tartalom. Elképzelhető, hogy időközben átneveztük, vagy hibás a hivatkozás, amelyről ide jutott.',
  'page.notFound.backHome': 'Vissza a kezdőlapra',
};
